import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { FaCalendarAlt, FaSpinner, FaBolt, FaWrench, FaSync, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import pythonApiClient from '../../services/api/pythonBaseApi';
import useRrhhProyecto from '../../hooks/useRrhhProyecto';
import RrhhProyectoSelector from '../../components/rrhh/RrhhProyectoSelector';
import { rrhhApiConfig } from '../../utils/rrhhProyectoHelpers';

// ─────────────────────────────────────────────────────────────────────────────
const DIAS_SEMANA = ['D','L','M','M','J','V','S'];
const MESES_ES   = ['','Enero','Febrero','Marzo','Abril','Mayo','Junio',
                    'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const showToast = (type, msg) =>
  toast[type]?.(msg, { position: 'bottom-right', autoClose: 4000 });

// ─────────────────────────────────────────────────────────────────────────────
// Helpers de fecha
// ─────────────────────────────────────────────────────────────────────────────
function diasEnMes(anio, mes) {
  return new Date(anio, mes, 0).getDate();   // mes es 1-indexed
}
function diaSemana(anio, mes, dia) {
  return new Date(anio, mes - 1, dia).getDay(); // 0=Dom … 6=Sab
}
function esFinDeSemana(anio, mes, dia) {
  const d = diaSemana(anio, mes, dia);
  return d === 0 || d === 6;
}

// ─────────────────────────────────────────────────────────────────────────────
// Celda de tipo de día
// ─────────────────────────────────────────────────────────────────────────────
const CeldaDia = ({ tipo, editado, esFS }) => {
  if (!tipo) return (
    <td style={{
      background: esFS ? '#f3f0ff' : '#f9fafb',
      borderRight: '1px solid #e5e7eb',
      minWidth: 28,
    }} />
  );

  const bg = tipo === 'T'
    ? (editado ? '#bbf7d0' : '#4ade80')    // T=verde, editado=verde claro
    : (editado ? '#fed7aa' : '#fb923c');    // D=naranja, editado=naranja claro

  return (
    <td style={{
      background: bg,
      textAlign: 'center',
      fontWeight: 700,
      fontSize: 11,
      color: '#fff',
      borderRight: '1px solid rgba(0,0,0,0.06)',
      minWidth: 28,
      padding: '4px 0',
      userSelect: 'none',
      cursor: 'default',
      textShadow: '0 1px 2px rgba(0,0,0,0.18)',
    }}>
      {tipo}
    </td>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Componente Principal
// ─────────────────────────────────────────────────────────────────────────────
const Ruster = () => {
  const {
    isCorporativo,
    proyectoId,
    setProyectoId,
    proyectos,
    loadingProyectos,
    needsProyectoSelection,
  } = useRrhhProyecto();

  const rrhhHeaders = useMemo(() => rrhhApiConfig(proyectoId), [proyectoId]);

  useEffect(() => {
    setPeriodos([]);
    setDetalle(null);
    setResultado(null);
    setErrorGen(null);
  }, [proyectoId]);

  // ── Estado de generación ────────────────────────────────────────────────
  const [generating, setGenerating] = useState(false);
  const [resultado,  setResultado]  = useState(null);
  const [errorGen,   setErrorGen]   = useState(null);

  // ── Estado de la grilla ─────────────────────────────────────────────────
  const hoy = new Date();
  const [anioGrilla, setAnioGrilla] = useState(hoy.getFullYear());
  const [mesGrilla,  setMesGrilla]  = useState(hoy.getMonth() + 1);
  const [periodos,   setPeriodos]   = useState([]);
  const [detalle,    setDetalle]    = useState(null);   // { periodo, empleados }
  const [loadingDet, setLoadingDet] = useState(false);
  const [errorDet,   setErrorDet]   = useState(null);

  // ── Cargar periodos ─────────────────────────────────────────────────────
  const fetchPeriodos = useCallback(async () => {
    if (!proyectoId) return;
    try {
      const res = await pythonApiClient.get('/rrhh/ruster/periodos', rrhhHeaders);
      setPeriodos(res.data || []);
    } catch { /* sin RUSTER generado aún */ }
  }, [proyectoId, rrhhHeaders]);

  useEffect(() => { fetchPeriodos(); }, [fetchPeriodos]);

  // ── Cargar detalle cuando cambia mes/año ───────────────────────────────
  const fetchDetalle = useCallback(async (anio, mes) => {
    if (!proyectoId) return;
    const p = periodos.find(x => x.anio === anio && x.mes === mes);
    if (!p) { setDetalle(null); return; }
    setLoadingDet(true);
    setErrorDet(null);
    try {
      const res = await pythonApiClient.get(`/rrhh/ruster/detalle/${p.id}`, rrhhHeaders);
      setDetalle(res.data);
    } catch (e) {
      setErrorDet(e.response?.data?.detail || 'Error cargando detalle');
    } finally {
      setLoadingDet(false);
    }
  }, [periodos, proyectoId, rrhhHeaders]);

  useEffect(() => { fetchDetalle(anioGrilla, mesGrilla); }, [anioGrilla, mesGrilla, fetchDetalle]);

  // ── Navegar meses ───────────────────────────────────────────────────────
  const navMes = (delta) => {
    let m = mesGrilla + delta, a = anioGrilla;
    if (m > 12) { m = 1;  a++; }
    if (m <  1) { m = 12; a--; }
    setMesGrilla(m); setAnioGrilla(a);
  };

  // ── Generar Ruster Global ───────────────────────────────────────────────
  const handleGenerarGlobal = useCallback(async () => {
    if (!proyectoId) {
      showToast('warning', 'Seleccione un proyecto');
      return;
    }
    setGenerating(true);
    setResultado(null);
    setErrorGen(null);
    try {
      const res = await pythonApiClient.post('/rrhh/ruster/generar-global', null, rrhhHeaders);
      setResultado(res.data?.resumen || null);
      showToast('success', res.data?.message || 'RUSTER generado correctamente');
      const resP = await pythonApiClient.get('/rrhh/ruster/periodos', rrhhHeaders);
      setPeriodos(resP.data || []);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Error al generar el RUSTER';
      setErrorGen(msg);
      showToast('error', msg);
    } finally {
      setGenerating(false);
    }
  }, [proyectoId, rrhhHeaders]);

  // ── Preparar datos de la grilla ─────────────────────────────────────────
  const totalDias = diasEnMes(anioGrilla, mesGrilla);
  const diasArr   = Array.from({ length: totalDias }, (_, i) => i + 1);

  // Map empleado_id → { fecha → tipo_dia }
  const empleadosData = detalle?.empleados?.map(emp => {
    const map = {};
    (emp.dias || []).forEach(d => { map[d.fecha] = { tipo: d.tipo_dia, editado: d.editado }; });
    return { ...emp, map };
  }) || [];

  const periodoPeriodo = detalle?.periodo;

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap');
        .ruster-wrap { font-family:'Inter',sans-serif; background:linear-gradient(135deg,#f8f9fa,#e9ecef); min-height:100vh; }
        .ruster-card { border:none; border-radius:20px; box-shadow:0 15px 35px rgba(0,0,0,.1); background:#fff; overflow:hidden; }
        .ruster-header { background:linear-gradient(135deg,#667eea,#764ba2); color:#fff; padding:1.5rem 2rem; }
        .ruster-header h4 { margin:0; font-weight:700; font-size:1.5rem; }
        .ruster-header p  { margin:.25rem 0 0; opacity:.85; font-size:.9rem; }
        .btn-ruster { border:none; border-radius:12px; padding:11px 20px; font-weight:700;
          cursor:pointer; display:inline-flex; align-items:center; gap:8px; font-size:.92rem;
          transition:all .25s; white-space:nowrap; }
        .btn-global  { background:linear-gradient(135deg,#11998e,#38ef7d); color:#fff;
          box-shadow:0 4px 14px rgba(17,153,142,.35); }
        .btn-global:hover:not(:disabled) { transform:translateY(-2px); box-shadow:0 8px 22px rgba(17,153,142,.45); }
        .btn-global:disabled { background:linear-gradient(135deg,#9ca3af,#6b7280); cursor:not-allowed; box-shadow:none; }
        .btn-off { background:linear-gradient(135deg,#d1d5db,#9ca3af); color:#fff;
          cursor:not-allowed; opacity:.7; box-shadow:none; }
        .resumen-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(155px,1fr)); gap:14px; margin-top:20px; }
        .stat-box { background:#f9fafb; border-radius:14px; padding:18px; text-align:center;
          border:2px solid #e5e7eb; transition:border-color .2s; }
        .stat-box:hover { border-color:#667eea; }
        .stat-num { font-size:2.2rem; font-weight:800; line-height:1; margin-bottom:5px; }
        .stat-lbl { font-size:.75rem; font-weight:700; color:#6b7280; text-transform:uppercase; letter-spacing:.06em; }
        .c-green { color:#059669; } .c-blue { color:#2563eb; } .c-purple { color:#7c3aed; }
        .c-orange { color:#d97706; } .c-teal { color:#0891b2; }

        /* Grilla */
        .grilla-card { background:#fff; border-radius:16px; box-shadow:0 4px 20px rgba(0,0,0,.07);
          overflow:hidden; margin-top:24px; }
        .grilla-nav { background:linear-gradient(135deg,#667eea,#764ba2); color:#fff;
          display:flex; align-items:center; justify-content:center; gap:20px; padding:12px 24px; }
        .grilla-nav-btn { background:rgba(255,255,255,.2); border:none; color:#fff;
          border-radius:8px; padding:6px 12px; cursor:pointer; display:flex; align-items:center;
          transition:background .2s; }
        .grilla-nav-btn:hover { background:rgba(255,255,255,.35); }
        .grilla-mes { font-size:1.15rem; font-weight:800; letter-spacing:.04em; }
        .grilla-scroll { overflow-x:auto; }
        .grilla-table { border-collapse:collapse; font-size:12px; min-width:100%; }
        .grilla-table th, .grilla-table td { border:none; }
        .th-emp { position:sticky; left:0; z-index:3; background:#1e293b; color:#fff;
          font-weight:700; padding:6px 14px; font-size:11px; text-align:left;
          border-right:2px solid #334155; white-space:nowrap; min-width:200px; }
        .th-dia { text-align:center; padding:0; vertical-align:bottom; min-width:28px; }
        .th-dia-inner { display:flex; flex-direction:column; align-items:center;
          padding:4px 0; gap:1px; }
        .th-dia-num { font-weight:800; font-size:12px; }
        .th-dia-sem { font-size:9px; font-weight:600; opacity:.75; }

        .td-emp { position:sticky; left:0; z-index:1; background:#f8fafc;
          border-right:2px solid #e2e8f0; padding:4px 12px; font-size:11px;
          font-weight:600; color:#374151; white-space:nowrap; border-bottom:1px solid #f1f5f9; }
        .fila-emp:hover .td-emp { background:#f0f4ff; }
        .fila-emp:hover td { filter:brightness(.96); }

        .leyenda { display:flex; gap:14px; align-items:center; padding:10px 16px;
          background:#f9fafb; border-top:1px solid #e5e7eb; }
        .ley-item { display:flex; align-items:center; gap:5px; font-size:11px; font-weight:600; color:#374151; }
        .ley-box { width:16px; height:16px; border-radius:4px; flex-shrink:0; }
      `}</style>

      <div className="ruster-wrap">
        <div className="container-fluid py-4">
          <ToastContainer />
          <RrhhProyectoSelector
            isCorporativo={isCorporativo}
            proyectos={proyectos}
            proyectoId={proyectoId}
            onChange={setProyectoId}
            loading={loadingProyectos}
          />
          {needsProyectoSelection && (
            <div className="alert alert-info mb-3">Seleccione un proyecto para gestionar el RUSTER.</div>
          )}
          <div className="ruster-card">

            {/* ── Header con botones ── */}
            <div className="ruster-header d-flex align-items-center justify-content-between flex-wrap" style={{ gap:12 }}>
              <div className="d-flex align-items-center gap-3">
                <FaCalendarAlt size={30} />
                <div>
                  <h4>RUSTER</h4>
                  <p>Registro diario de Trabajo / Descanso por empleado</p>
                </div>
              </div>
              <div className="d-flex gap-2 flex-wrap">
                <button className="btn-ruster btn-global" onClick={handleGenerarGlobal} disabled={generating}
                  title="Genera períodos y días T/D para todos los empleados activos (rango -3 / +3 meses)">
                  {generating ? <><FaSpinner className="fa-spin" />Generando…</> : <><FaBolt />Generar Ruster Global</>}
                </button>
                <button className="btn-ruster btn-off" disabled title="Próximamente">
                  <FaWrench />Generar Ruster Personalizado
                </button>
                <button className="btn-ruster btn-off" disabled title="Próximamente">
                  <FaSync />Actualizar Ruster
                </button>
              </div>
            </div>

            {/* ── Cuerpo superior ── */}
            <div style={{ padding:'2rem' }}>
              {/* ── Spinner al generar ── */}
              {generating && (
                <div style={{ textAlign:'center', padding:'40px 0' }}>
                  <FaSpinner size={44} className="fa-spin" style={{ color:'#667eea', marginBottom:14 }} />
                  <p style={{ color:'#374151', fontWeight:600 }}>Generando RUSTER Global…</p>
                  <p style={{ color:'#9ca3af', fontSize:'.9rem' }}>Calculando ciclos T/D por régimen de cada empleado</p>
                </div>
              )}

              {/* ── Error de generación ── */}
              {errorGen && !generating && (
                <div style={{ background:'#fef2f2', border:'2px solid #fca5a5', borderRadius:12,
                  padding:'14px 20px', color:'#dc2626', fontWeight:500, marginBottom:8 }}>⚠️ {errorGen}</div>
              )}

              {/* ── Sin RUSTER aún (servidor sin períodos y sin resultado en sesión) ── */}
              {!generating && !resultado && !errorGen && periodos.length === 0 && (
                <div style={{ textAlign:'center', padding:'40px 0' }}>
                  <FaCalendarAlt size={56} style={{ color:'#d1d5db', marginBottom:14 }} />
                  <h5 style={{ color:'#374151', fontWeight:700, marginBottom:8 }}>RUSTER no generado aún</h5>
                  <p style={{ color:'#9ca3af', maxWidth:420, margin:'0 auto', lineHeight:1.6 }}>
                    Haz clic en <strong>Generar Ruster Global</strong> para crear automáticamente los períodos
                    y asignar los días T/D según el régimen de cada empleado activo (rango −3 a +3 meses).
                  </p>
                </div>
              )}

              {/* ── RUSTER ya existe en BD pero no se regeneró en esta sesión ── */}
              {!generating && !resultado && !errorGen && periodos.length > 0 && (
                <div style={{ background:'linear-gradient(135deg,#eff6ff,#dbeafe)', border:'2px solid #3b82f6',
                  borderRadius:14, padding:'14px 22px', display:'flex', alignItems:'center', gap:14, flexWrap:'wrap' }}>
                  <FaCalendarAlt size={22} style={{ color:'#2563eb', flexShrink:0 }} />
                  <div style={{ flex:1 }}>
                    <p style={{ margin:0, fontWeight:700, color:'#1e40af', fontSize:'1rem' }}>
                      ✅ El RUSTER ya está generado
                    </p>
                    <p style={{ margin:0, color:'#3b82f6', fontSize:'.85rem', marginTop:2 }}>
                      {periodos.length} período{periodos.length !== 1 ? 's' : ''} disponibles
                      {' · '}
                      {periodos[periodos.length - 1]?.nombre} → {periodos[0]?.nombre}
                    </p>
                  </div>
                  <span style={{ fontSize:'.8rem', color:'#6b7280', fontStyle:'italic' }}>
                    Puedes volver a ejecutar <strong>Generar Ruster Global</strong> para añadir nuevos empleados
                    o períodos sin perder datos editados.
                  </span>
                </div>
              )}

              {/* ── Recién generado en esta sesión → 5 tarjetas de estadísticas ── */}
              {resultado && !generating && (
                <>
                  <div style={{ background:'linear-gradient(135deg,#ecfdf5,#d1fae5)', border:'2px solid #059669',
                    borderRadius:14, padding:'14px 22px', display:'flex', alignItems:'center', gap:12,
                    color:'#065f46', fontWeight:700, fontSize:'1rem', marginBottom:4 }}>
                    ✅ RUSTER generado correctamente
                    <span style={{ marginLeft:'auto', fontSize:'.82rem', fontWeight:500, color:'#047857' }}>
                      Rango: {resultado.rango_desde} → {resultado.rango_hasta}
                    </span>
                  </div>
                  <div className="resumen-grid">
                    {[
                      { lbl:'Meses en rango',      val:resultado.meses_en_rango,                   cls:'c-purple' },
                      { lbl:'Períodos creados',     val:resultado.periodos_creados,                  cls:'c-green'  },
                      { lbl:'Períodos existentes',  val:resultado.periodos_existentes,               cls:'c-blue'   },
                      { lbl:'Empleados procesados', val:resultado.empleados_procesados,              cls:'c-orange' },
                      { lbl:'Días generados',       val:resultado.dias_generados?.toLocaleString(), cls:'c-teal'   },
                    ].map(s => (
                      <div key={s.lbl} className="stat-box">
                        <div className={`stat-num ${s.cls}`}>{s.val}</div>
                        <div className="stat-lbl">{s.lbl}</div>
                      </div>
                    ))}
                  </div>
                  <p style={{ color:'#9ca3af', fontSize:'.8rem', marginTop:14, textAlign:'center' }}>
                    Los días ya editados manualmente no fueron sobreescritos (INSERT IGNORE). Puedes volver a ejecutar sin riesgo.
                  </p>
                </>
              )}
            </div>

            {/* ══════════════════════════════════════════════════════════════
                GRILLA MENSUAL
            ══════════════════════════════════════════════════════════════ */}
            <div style={{ padding:'0 2rem 2rem' }}>
              <div className="grilla-card">

                {/* Navegación de mes */}
                <div className="grilla-nav">
                  <button className="grilla-nav-btn" onClick={() => navMes(-1)}>
                    <FaChevronLeft size={14} />
                  </button>
                  <span className="grilla-mes">
                    {MESES_ES[mesGrilla].toUpperCase()} {anioGrilla}
                  </span>
                  <button className="grilla-nav-btn" onClick={() => navMes(1)}>
                    <FaChevronRight size={14} />
                  </button>
                </div>

                {/* Estados de carga / sin data */}
                {loadingDet && (
                  <div style={{ textAlign:'center', padding:40 }}>
                    <FaSpinner className="fa-spin" size={32} style={{ color:'#667eea' }} />
                    <p style={{ color:'#9ca3af', marginTop:10 }}>Cargando datos del período…</p>
                  </div>
                )}
                {!loadingDet && errorDet && (
                  <div style={{ padding:24, color:'#dc2626', fontWeight:600 }}>⚠️ {errorDet}</div>
                )}
                {!loadingDet && !errorDet && !detalle && (
                  <div style={{ textAlign:'center', padding:40 }}>
                    <FaCalendarAlt size={40} style={{ color:'#d1d5db', marginBottom:10 }} />
                    <p style={{ color:'#9ca3af', fontWeight:600 }}>
                      Sin datos para {MESES_ES[mesGrilla]} {anioGrilla}
                    </p>
                    <p style={{ color:'#c4c4c4', fontSize:'.85rem' }}>
                      Genera el RUSTER Global para poblar este período.
                    </p>
                  </div>
                )}
                {!loadingDet && !errorDet && detalle && (
                  <>
                    <div className="grilla-scroll">
                      <table className="grilla-table">
                        <thead>
                          <tr>
                            {/* Columna fija empleado */}
                            <th className="th-emp" style={{ background:'#1e293b', padding:'10px 14px' }}>
                              EMPLEADOS
                            </th>
                            {diasArr.map(d => {
                              const fs = esFinDeSemana(anioGrilla, mesGrilla, d);
                              const ds = DIAS_SEMANA[diaSemana(anioGrilla, mesGrilla, d)];
                              return (
                                <th key={d} className="th-dia"
                                  style={{ background: fs ? '#7f1d1d' : '#1e293b',
                                    color: fs ? '#fca5a5' : '#e2e8f0', padding:'6px 2px' }}>
                                  <div className="th-dia-inner">
                                    <span className="th-dia-num">{d}</span>
                                    <span className="th-dia-sem">{ds}</span>
                                  </div>
                                </th>
                              );
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {empleadosData.length === 0 ? (
                            <tr>
                              <td colSpan={totalDias + 1} style={{ textAlign:'center', padding:24,
                                color:'#9ca3af', fontWeight:600 }}>
                                Sin empleados con registro en este período
                              </td>
                            </tr>
                          ) : (
                            empleadosData.map((emp, ei) => (
                              <tr key={emp.empleado_id} className="fila-emp"
                                style={{ background: ei % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                <td className="td-emp">
                                  {emp.numero_documento_identidad} {emp.nombre_completo}
                                </td>
                                {diasArr.map(d => {
                                  const key = `${anioGrilla}-${String(mesGrilla).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
                                  const info = emp.map[key];
                                  const fs   = esFinDeSemana(anioGrilla, mesGrilla, d);
                                  return <CeldaDia key={d} tipo={info?.tipo} editado={info?.editado} esFS={fs} />;
                                })}
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Leyenda */}
                    <div className="leyenda">
                      <span style={{ fontWeight:700, fontSize:11, color:'#374151', marginRight:4 }}>LEYENDA:</span>
                      <div className="ley-item"><div className="ley-box" style={{ background:'#4ade80' }} />T — Trabajo</div>
                      <div className="ley-item"><div className="ley-box" style={{ background:'#fb923c' }} />D — Descanso</div>
                      <div className="ley-item"><div className="ley-box" style={{ background:'#bbf7d0' }} />T (modificado)</div>
                      <div className="ley-item"><div className="ley-box" style={{ background:'#fed7aa' }} />D (modificado)</div>
                      <div className="ley-item" style={{ marginLeft:'auto', color:'#7c3aed', fontWeight:700 }}>
                        Régimen → los empleados muestran el código arriba
                      </div>
                    </div>

                    {/* ─── Tabla Resumen ─── */}
                    <div style={{ padding:'0 0 0 0', overflowX:'auto' }}>
                      <table style={{ borderCollapse:'collapse', width:'100%', fontSize:12 }}>
                        {/* Fila separadora */}
                        <tbody>
                          <tr>
                            <td colSpan={4} style={{ padding:'14px 0', background:'#f1f5f9', borderTop:'3px solid #e2e8f0' }} />
                          </tr>
                          {/* Fila de título RESUMEN */}
                          <tr>
                            <td colSpan={4} style={{
                              background:'linear-gradient(135deg,#334155,#1e293b)',
                              color:'#e2e8f0',
                              fontWeight:800,
                              fontSize:13,
                              letterSpacing:'.1em',
                              padding:'10px 16px',
                              textTransform:'uppercase',
                            }}>
                              📊 Resumen — {MESES_ES[mesGrilla]} {anioGrilla}
                            </td>
                          </tr>
                        </tbody>
                        <tbody>
                          <tr>
                            <th style={{ background:'#1e293b', color:'#fff', padding:'10px 14px',
                              textAlign:'left', fontWeight:700, fontSize:11, minWidth:200,
                              position:'sticky', left:0, zIndex:2 }}>
                              EMPLEADOS
                            </th>
                            <th style={{ background:'#16a34a', color:'#fff', padding:'10px 18px',
                              textAlign:'center', fontWeight:700, fontSize:11, whiteSpace:'nowrap' }}>
                              DÍAS TRABAJADOS
                            </th>
                            <th style={{ background:'#ea580c', color:'#fff', padding:'10px 18px',
                              textAlign:'center', fontWeight:700, fontSize:11, whiteSpace:'nowrap' }}>
                              DÍAS DESCANSO
                            </th>
                            <th style={{ background:'#64748b', color:'#fff', padding:'10px 18px',
                              textAlign:'center', fontWeight:700, fontSize:11, whiteSpace:'nowrap' }}>
                              DÍAS FUERA DE LABOR
                            </th>
                          </tr>
                        </tbody>
                        <tbody>
                          {empleadosData.map((emp, ei) => {
                            let t = 0, d = 0;
                            diasArr.forEach(dia => {
                              const key = `${anioGrilla}-${String(mesGrilla).padStart(2,'0')}-${String(dia).padStart(2,'0')}`;
                              const info = emp.map[key];
                              if (info?.tipo === 'T') t++;
                              else if (info?.tipo === 'D') d++;
                            });
                            const fuera = totalDias - t - d;
                            return (
                              <tr key={emp.empleado_id}
                                style={{ background: ei % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                <td style={{ padding:'7px 14px', fontWeight:600, color:'#374151',
                                  borderBottom:'1px solid #f1f5f9', position:'sticky', left:0,
                                  background: ei % 2 === 0 ? '#fff' : '#f8fafc', zIndex:1 }}>
                                  {emp.numero_documento_identidad} {emp.nombre_completo}
                                </td>
                                <td style={{ textAlign:'center', fontWeight:700, color:'#15803d',
                                  borderBottom:'1px solid #f1f5f9', padding:'7px 0' }}>{t}</td>
                                <td style={{ textAlign:'center', fontWeight:700, color:'#c2410c',
                                  borderBottom:'1px solid #f1f5f9', padding:'7px 0' }}>{d}</td>
                                <td style={{ textAlign:'center', fontWeight:700, color:'#475569',
                                  borderBottom:'1px solid #f1f5f9', padding:'7px 0' }}>{fuera}</td>
                              </tr>
                            );
                          })}
                          {/* Fila de totales */}
                          {empleadosData.length > 0 && (() => {
                            let totT = 0, totD = 0, totF = 0;
                            empleadosData.forEach(emp => {
                              diasArr.forEach(dia => {
                                const key = `${anioGrilla}-${String(mesGrilla).padStart(2,'0')}-${String(dia).padStart(2,'0')}`;
                                const info = emp.map[key];
                                if (info?.tipo === 'T') totT++;
                                else if (info?.tipo === 'D') totD++;
                              });
                              totF += totalDias - diasArr.filter(dia => {
                                const key = `${anioGrilla}-${String(mesGrilla).padStart(2,'0')}-${String(dia).padStart(2,'0')}`;
                                return !!emp.map[key];
                              }).length;
                            });
                            return (
                              <tr style={{ background:'#1e293b' }}>
                                <td style={{ padding:'8px 14px', color:'#fff', fontWeight:800,
                                  fontSize:11, position:'sticky', left:0, background:'#1e293b', zIndex:1 }}>
                                  TOTALES
                                </td>
                                <td style={{ textAlign:'center', fontWeight:800, color:'#4ade80', padding:'8px 0' }}>{totT}</td>
                                <td style={{ textAlign:'center', fontWeight:800, color:'#fb923c', padding:'8px 0' }}>{totD}</td>
                                <td style={{ textAlign:'center', fontWeight:800, color:'#94a3b8', padding:'8px 0' }}>{totF}</td>
                              </tr>
                            );
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default Ruster;
